/**
 * One answer to "did this fail because of the AI service or the network, and
 * is waiting likely to fix it?", for the three places that must agree: the
 * Quote Run queue, video evidence preparation and PDF evidence preparation.
 *
 * Why it exists. `classifyAiError` only recognises failures it has operator
 * wording for. The gateway also sends `ai-provider-timeout`,
 * `ai-provider-invalid-response` and `ai-provider-stream` (all retryable), and
 * a dropped connection or an expired session carries no category at all. Each
 * lane kept its own list of "transient" kinds, so those failures were treated
 * as a fault in the FILE: a site video was recorded as "this browser could not
 * process the clip" because the gateway timed out once.
 */

import { classifyAiError } from './ai-error-handling';

export interface AiServiceFailure {
    /** The AI service, the gateway or the network failed, not this input. */
    fromService: boolean;
    /** Waiting and retrying may fix it. False means it needs the operator. */
    transient: boolean;
    kind: string | null;
    /** Plain words for an operator-facing note. */
    title: string | null;
}

const TRANSIENT_KINDS = new Set([
    'ai-provider-keys-quarantined',
    'ai-provider-rate-limit',
    'ai-provider-model-busy',
    'ai-provider-unavailable',
    'ai-provider-timeout',
    'ai-provider-invalid-response',
    'ai-provider-stream',
]);

const TRANSIENT_CODES: Record<string, string> = {
    upstream_timeout: 'ai-provider-timeout',
    upstream_invalid_json: 'ai-provider-invalid-response',
    upstream_stream_error: 'ai-provider-stream',
};

const FALLBACK_TITLES: Record<string, string> = {
    'ai-provider-timeout': 'AI provider did not respond in time',
    'ai-provider-invalid-response': 'AI provider returned an incomplete response',
    'ai-provider-stream': 'AI provider ended the response early',
    'ai-provider-unavailable': 'AI provider is temporarily unavailable',
    network: 'the connection dropped',
    'session-auth': 'the QuoteMaker session was refused',
};

const NOT_FROM_SERVICE: AiServiceFailure = { fromService: false, transient: false, kind: null, title: null };

function fromService(kind: string, transient: boolean, title?: string | null): AiServiceFailure {
    return { fromService: true, transient, kind, title: title || FALLBACK_TITLES[kind] || null };
}

type Failure = Parameters<typeof classifyAiError>[0] & { name?: unknown };

/**
 * A 200 with nothing in it. Live, 2026-09-20, cloud run during an OpenRouter
 * rate-limit episode: Hornsby waited out a 429 and a key quarantine, then failed
 * outright on "empty assistant content ... finishReason=error ... tokensIn=0,
 * tokensOut=0". The provider reported an error and read nothing of the prompt,
 * so nothing about the project was judged. It was recorded as the project's
 * fault, and the next project ran fine ninety seconds later.
 *
 * Deliberately narrow: an empty reply with a normal finish, or one that did
 * read the prompt, is the model choosing to say nothing (a refusal, a bad
 * prompt) and waiting will not change it.
 */
function providerFailedWithoutAnswering(err: Record<string, unknown>): boolean {
    if (String(err.code || '') !== 'AI_EMPTY_CONTENT') return false;
    const message = String(err.message || '');
    // Fields when the thrower set them; the message is the fallback so an
    // error rebuilt from a stored job record still classifies the same way.
    const finishReason = String(err.finishReason ?? (message.match(/finishReason=([A-Za-z_]+)/)?.[1] ?? '')).toLowerCase();
    const tokens = (field: 'tokensIn' | 'tokensOut'): number | null => {
        const direct = err[field];
        if (typeof direct === 'number' && Number.isFinite(direct)) return direct;
        const parsed = message.match(new RegExp(`${field}=(\\d+)`))?.[1];
        return parsed == null ? null : Number(parsed);
    };
    return finishReason === 'error' || (tokens('tokensIn') === 0 && tokens('tokensOut') === 0);
}

export function isTransientAiServiceKind(kind: unknown): boolean {
    return TRANSIENT_KINDS.has(String(kind || ''));
}

export function classifyAiServiceFailure(error: unknown): AiServiceFailure {
    const err = (error && typeof error === 'object' ? error : {}) as NonNullable<Failure>;
    const classified = classifyAiError(err);
    if (classified.isAiProviderError && classified.kind) {
        // A request that is too large is about THIS input, not the service.
        if (classified.kind === 'ai-request-too-large') return NOT_FROM_SERVICE;
        return fromService(classified.kind, TRANSIENT_KINDS.has(classified.kind), classified.title);
    }
    if (providerFailedWithoutAnswering(err as Record<string, unknown>)) {
        return fromService('ai-provider-invalid-response', true);
    }
    const category = String(err.category || '').trim();
    if (TRANSIENT_KINDS.has(category)) return fromService(category, true);
    const codeKind = TRANSIENT_CODES[String(err.code || '').trim()];
    if (codeKind) return fromService(codeKind, true);
    // An error that names its own category as something else (project
    // coordination, persistence, intake readiness) has already said what it is.
    // A bare HTTP status on it must not be read as an AI-service outage: a
    // claim-renewal failure carries 503 and is not something to wait on.
    if (category && !category.startsWith('ai-')) return NOT_FROM_SERVICE;

    const status = Number(err.status || err.statusCode || 0);
    // The QuoteMaker session itself (not a provider key) was refused.
    if (status === 401 || status === 403) return fromService('session-auth', false);
    if (status === 408 || status === 504) return fromService('ai-provider-timeout', true);
    if (status >= 500) return fromService('ai-provider-unavailable', true);

    // fetch() rejects with a TypeError when the connection drops.
    const message = String(err.message || '');
    if (err.name === 'TypeError' && /failed to fetch|networkerror|network request failed|load failed/i.test(message)) {
        return fromService('network', true);
    }
    return NOT_FROM_SERVICE;
}

/**
 * A generator that catches an API error and returns a fallback document hides
 * the cause. In a queue run the caller must surface the REAL error when it was
 * the AI service, so the queue can wait and retry (or state the true reason),
 * instead of reporting a generic 'no result'. Returns that error, or null when
 * the fallback had some other cause.
 */
export function serviceFailureBehindFallback(result: { apiError?: unknown } | null | undefined): unknown | null {
    const apiError = result?.apiError;
    return apiError && classifyAiServiceFailure(apiError).fromService ? apiError : null;
}

/**
 * Counts CONSECUTIVE failures per key, in memory, and forgets a count that has
 * gone quiet. Without the expiry a tab left open kept a morning's count all
 * day: one brief wobble hours later then read as "failed three times in a row"
 * and the file was given up on at once.
 */
export function createConsecutiveFailureCounter(quietAfterMs: number) {
    const counts = new Map<string, { count: number; at: number }>();
    return {
        increment(key: string): number {
            const now = Date.now();
            const prior = counts.get(key);
            const count = (prior && now - prior.at < quietAfterMs ? prior.count : 0) + 1;
            counts.set(key, { count, at: now });
            return count;
        },
        clear(key: string): void { counts.delete(key); },
        reset(): void { counts.clear(); },
    };
}
